import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Check, X, Loader2, CreditCard, Users, Calendar, RefreshCw, AlertTriangle, Eye, EyeOff, Edit3, TrendingUp, Link as LinkIcon } from 'lucide-react';
import MoneyPinGate, { getMoneyStamp } from './MoneyPinGate';

const API = process.env.REACT_APP_BACKEND_URL;

const yyyymm = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export default function SquareSettings() {
  const [accounts, setAccounts] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ label: '', application_id: '', access_token: '', location_id: '', webhook_signature_key: '', auto_confirm_enabled: false, auto_confirm_template: 'Booking confirmed for {date}. Reply: m.me/okiescape' });
  const [showToken, setShowToken] = useState(false);
  const [showSig, setShowSig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Data tabs
  const [tab, setTab] = useState('accounts');
  const [bookings, setBookings] = useState(null);
  const [bookingsMonth, setBookingsMonth] = useState(yyyymm(new Date()));
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const fetchAccounts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/square/accounts`, { withCredentials: true });
      setAccounts(data.accounts);
      setActiveId(data.active_id);
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { fetchAccounts(); }, []);

  const resetForm = () => {
    setForm({ label: '', application_id: '', access_token: '', location_id: '', webhook_signature_key: '', auto_confirm_enabled: false, auto_confirm_template: 'Booking confirmed for {date}. Reply: m.me/okiescape' });
    setEditingId(null);
    setAdding(false);
  };

  const saveAccount = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/api/admin/square/accounts/${editingId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/square/accounts`, form, { withCredentials: true });
      }
      resetForm();
      fetchAccounts();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Remove this Square account?')) return;
    await axios.delete(`${API}/api/admin/square/accounts/${id}`, { withCredentials: true });
    fetchAccounts();
  };

  const setActive = async (id) => {
    await axios.post(`${API}/api/admin/square/active`, { id }, { withCredentials: true });
    setActiveId(id);
    setTestResult(null);
    setBookings(null);
    setCustomers(null);
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setAdding(true);
    setForm({
      label: a.label || '',
      application_id: a.application_id || '',
      access_token: '',
      location_id: a.location_id || '',
      webhook_signature_key: '',
      auto_confirm_enabled: !!a.auto_confirm_enabled,
      auto_confirm_template: a.auto_confirm_template || '',
    });
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/test`, { withCredentials: true });
      setTestResult({ ok: true, data });
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || 'Test failed' });
    }
    setTesting(false);
  };

  const fetchBookings = async () => {
    setBookingsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/bookings?month=${bookingsMonth}`, { withCredentials: true });
      setBookings(data);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Fetch failed');
    }
    setBookingsLoading(false);
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/customers`, { withCredentials: true });
      setCustomers(data);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Fetch failed');
    }
    setCustomersLoading(false);
  };

  const fetchRevenue = async () => {
    const stamp = getMoneyStamp();
    if (!stamp) { setRevenue(null); return; }
    setRevenueLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/revenue`, {
        withCredentials: true,
        headers: { 'X-Money-Stamp': stamp },
      });
      setRevenue(data);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Fetch failed');
    }
    setRevenueLoading(false);
  };

  // Auto-load revenue when the Revenue tab becomes active OR when PIN is just entered
  const tryAutoRevenue = () => {
    if (tab === 'revenue' && !revenue && !revenueLoading && activeId && getMoneyStamp()) {
      fetchRevenue();
    }
  };
  useEffect(() => {
    tryAutoRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeId]);
  // Listen for PIN-unlock event from MoneyPinGate so we auto-load right after unlock
  useEffect(() => {
    const handler = () => tryAutoRevenue();
    window.addEventListener('oe-money-unlocked', handler);
    return () => window.removeEventListener('oe-money-unlocked', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeId, revenue, revenueLoading]);

  const activeAccount = accounts.find(a => a.id === activeId);
  const filteredCustomers = customers ? customers.customers.filter(c =>
    !customerQuery || c.name.toLowerCase().includes(customerQuery.toLowerCase()) || c.phone.includes(customerQuery)
  ) : [];

  if (loading) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-neutral-500 text-sm"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <CreditCard size={20} className="text-brand" />
        <h2 className="font-heading text-2xl tracking-wider uppercase">Square <span className="text-brand">Integration</span></h2>
      </div>

      {/* Active account switcher */}
      {accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-4 bg-card border border-border rounded-xl" data-testid="square-active-switcher">
          <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Active Account:</span>
          <select
            value={activeId}
            onChange={e => setActive(e.target.value)}
            data-testid="square-account-select"
            className="bg-background border border-border rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand"
          >
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <button
            onClick={testConnection}
            disabled={testing}
            data-testid="square-test-btn"
            className="ml-auto flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase bg-secondary text-foreground px-3 py-1.5 rounded-lg hover:bg-secondary/70 transition-colors disabled:opacity-50 border border-border"
          >
            {testing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          {testResult && (
            <span className={`font-sans text-xs ${testResult.ok ? 'text-green-400' : 'text-red-400'}`}>
              {testResult.ok ? `✓ ${testResult.data.location?.name || 'OK'}` : testResult.error}
            </span>
          )}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border">
        {[
          { id: 'accounts', label: 'Accounts', icon: CreditCard },
          { id: 'revenue', label: 'Revenue', icon: TrendingUp },
          { id: 'customers', label: 'Customers', icon: Users },
          { id: 'bookings', label: 'Bookings', icon: Calendar },
          { id: 'mapping', label: 'Service Map', icon: LinkIcon },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`square-subtab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 font-heading text-xs tracking-widest uppercase transition-colors ${tab === t.id ? 'text-brand border-b-2 border-brand' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* ACCOUNTS TAB */}
      {tab === 'accounts' && (
        <div className="space-y-4">
          {accounts.length === 0 && !adding && (
            <div className="border border-yellow-700/40 bg-yellow-900/10 rounded-xl p-5 flex items-start gap-3">
              <AlertTriangle size={18} className="text-yellow-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading text-sm tracking-wider uppercase text-yellow-500">No Square Accounts</p>
                <p className="font-sans text-xs text-muted-foreground mt-1">Add your first Square account to start syncing customers, bookings, and revenue.</p>
              </div>
            </div>
          )}

          {/* Account list */}
          <div className="space-y-3">
            {accounts.map(a => (
              <div key={a.id} className={`border rounded-xl p-4 ${a.id === activeId ? 'border-brand/60 bg-brand/5' : 'border-border bg-card'}`} data-testid={`square-account-${a.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading text-base tracking-wide">{a.label}</span>
                  {a.id === activeId && <span className="font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-brand/20 text-brand border border-brand/40">Active</span>}
                  {a.auto_confirm_enabled && <span className="font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Auto-Confirm SMS</span>}
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => startEdit(a)} className="p-1.5 text-muted-foreground hover:text-brand transition-colors" title="Edit"><Edit3 size={13} /></button>
                    <button onClick={() => deleteAccount(a.id)} className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors" title="Remove"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 font-mono text-[11px] text-muted-foreground">
                  <div>Location: <span className="text-foreground">{a.location_id}</span></div>
                  <div>App ID: <span className="text-foreground">{a.application_id || '—'}</span></div>
                  <div>Token: <span className="text-foreground">{a.access_token}</span></div>
                  <div>Webhook key: <span className="text-foreground">{a.webhook_signature_key || '—'}</span></div>
                </div>
              </div>
            ))}
          </div>

          {/* Add / edit form */}
          {adding ? (
            <div className="border-2 border-brand/40 bg-card rounded-xl p-5 space-y-3" data-testid="square-account-form">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-sm tracking-widest uppercase">{editingId ? 'Edit Account' : 'New Account'}</h3>
                <button onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Label</label>
                  <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Main / Rycom" className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand" />
                </div>
                <div>
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Location ID</label>
                  <input value={form.location_id} onChange={e => setForm(f => ({ ...f, location_id: e.target.value }))} placeholder="L1NXWGXTR4G2A" className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:border-brand" />
                </div>
              </div>
              <div>
                <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Application ID</label>
                <input value={form.application_id} onChange={e => setForm(f => ({ ...f, application_id: e.target.value }))} placeholder="sq0idp-..." className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Access Token {editingId && <span className="text-muted-foreground/60 normal-case font-sans">(leave blank to keep existing)</span>}</label>
                <div className="relative">
                  <input type={showToken ? 'text' : 'password'} value={form.access_token} onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} placeholder="EAAAl..." className="w-full bg-background border border-border rounded-lg px-3 py-2 pr-10 font-mono text-sm focus:outline-none focus:border-brand" />
                  <button onClick={() => setShowToken(!showToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showToken ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                </div>
              </div>
              <div>
                <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Webhook Signature Key {editingId && <span className="text-muted-foreground/60 normal-case font-sans">(leave blank to keep existing)</span>}</label>
                <div className="relative">
                  <input type={showSig ? 'text' : 'password'} value={form.webhook_signature_key} onChange={e => setForm(f => ({ ...f, webhook_signature_key: e.target.value }))} placeholder="Optional — from Square Webhooks dashboard" className="w-full bg-background border border-border rounded-lg px-3 py-2 pr-10 font-mono text-sm focus:outline-none focus:border-brand" />
                  <button onClick={() => setShowSig(!showSig)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showSig ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer pt-2">
                <input type="checkbox" checked={form.auto_confirm_enabled} onChange={e => setForm(f => ({ ...f, auto_confirm_enabled: e.target.checked }))} className="peer appearance-none w-4 h-4 border border-border rounded checked:bg-brand checked:border-brand transition-colors" />
                <span className="font-sans text-sm">Auto-send SMS confirmation when a booking is created</span>
              </label>
              {form.auto_confirm_enabled && (
                <div>
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Confirmation Template</label>
                  <textarea value={form.auto_confirm_template} onChange={e => setForm(f => ({ ...f, auto_confirm_template: e.target.value }))} rows={2} className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand resize-none" />
                  <p className="font-sans text-[10px] text-muted-foreground mt-1">Use <span className="font-mono">{'{date}'}</span> as a placeholder. SMS is sent via your configured Vonage account.</p>
                </div>
              )}
              <div className="flex items-center gap-3 pt-2">
                <button onClick={saveAccount} data-testid="square-save-account" className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors">{editingId ? 'Update' : 'Add'} Account</button>
                <button onClick={resetForm} className="font-heading text-xs tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="square-add-account-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase border border-dashed border-border rounded-lg px-4 py-3 text-muted-foreground hover:text-brand hover:border-brand transition-colors w-full justify-center">
              <Plus size={14} /> Add Square Account
            </button>
          )}

          {/* Webhook URL hint */}
          {accounts.length > 0 && (
            <div className="border border-border bg-card/50 rounded-xl p-4">
              <p className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Webhook URL — paste into Square Dashboard</p>
              <code className="font-mono text-xs text-brand break-all">{API}/api/webhooks/square</code>
              <p className="font-sans text-[10px] text-muted-foreground mt-2">In Square Dashboard → Developers → Webhooks → add this URL and subscribe to <span className="font-mono">booking.created</span>. Copy the signature key into the account above.</p>
            </div>
          )}
        </div>
      )}

      {/* REVENUE TAB */}
      {tab === 'revenue' && (
        <MoneyPinGate title="Reveal Revenue">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={fetchRevenue} disabled={revenueLoading} data-testid="square-fetch-revenue" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50">
                {revenueLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                {revenueLoading ? 'Loading...' : (revenue ? 'Refresh' : 'Load Revenue')}
              </button>
              {revenue && (
                <span className="font-sans text-[10px] text-muted-foreground">
                  Currency: <span className="font-mono">{revenue.currency}</span> · As of {new Date(revenue.fetched_at).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>

            {revenue ? (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="revenue-cards">
                  {[
                    { id: 'today', label: 'Today', data: revenue.today, accent: 'text-brand' },
                    { id: 'week', label: 'Last 7 Days', data: revenue.week, accent: 'text-yellow-400' },
                    { id: 'mtd', label: 'Month to Date', data: revenue.month_to_date, accent: 'text-green-400' },
                    { id: 'd30', label: 'Last 30 Days', data: revenue.month_30d, accent: 'text-purple-400' },
                  ].map(card => (
                    <div key={card.id} className="border border-border rounded-xl bg-card p-5" data-testid={`revenue-${card.id}`}>
                      <p className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">{card.label}</p>
                      <p className={`font-heading text-3xl mt-2 ${card.accent}`}>{card.data.formatted}</p>
                      <p className="font-sans text-[10px] text-muted-foreground mt-1">
                        {card.data.count} payment{card.data.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="font-sans text-[10px] text-muted-foreground">
                  Includes only completed/approved payments. Refunds and voided transactions are excluded.
                </p>
              </>
            ) : !revenueLoading && (
              <p className="font-sans text-xs text-muted-foreground italic">Click Refresh to load revenue data.</p>
            )}
          </div>
        </MoneyPinGate>
      )}

      {/* SERVICE MAP TAB */}
      {tab === 'mapping' && <ServiceMappingPanel />}


      {/* CUSTOMERS TAB */}
      {tab === 'customers' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={fetchCustomers} disabled={customersLoading} data-testid="square-fetch-customers" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50">
              {customersLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {customersLoading ? 'Loading...' : (customers ? 'Refresh' : 'Load Customers')}
            </button>
            {customers && <span className="font-sans text-xs text-muted-foreground">{customers.total} customers with phone numbers</span>}
          </div>
          {customers && (
            <>
              <input value={customerQuery} onChange={e => setCustomerQuery(e.target.value)} placeholder="Search by name or phone..." className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand" />
              <div className="border border-border rounded-xl bg-card overflow-hidden">
                <div className="max-h-[500px] overflow-y-auto">
                  {filteredCustomers.length === 0 ? (
                    <p className="font-sans text-xs text-muted-foreground italic text-center py-6">No matches.</p>
                  ) : filteredCustomers.map(c => (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-secondary/30 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center font-heading text-[10px] text-brand">
                        {c.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm truncate">{c.name}</p>
                        {c.email && <p className="font-sans text-[10px] text-muted-foreground truncate">{c.email}</p>}
                      </div>
                      <span className="font-mono text-xs text-foreground">{c.phone}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="font-sans text-[10px] text-muted-foreground">These numbers also appear as a dropdown on the <strong>Message the Customer</strong> tab.</p>
            </>
          )}
        </div>
      )}

      {/* BOOKINGS TAB */}
      {tab === 'bookings' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <input type="month" value={bookingsMonth} onChange={e => setBookingsMonth(e.target.value)} className="bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand" />
            <button onClick={fetchBookings} disabled={bookingsLoading} data-testid="square-fetch-bookings" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50">
              {bookingsLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {bookingsLoading ? 'Loading...' : 'Fetch'}
            </button>
            {bookings && <span className="font-sans text-xs text-muted-foreground">{bookings.total} bookings · {Object.keys(bookings.grouped).length} services</span>}
          </div>
          {bookings && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(bookings.grouped).map(([name, days]) => {
                const total = Object.values(days).reduce((a, b) => a + b, 0);
                return (
                  <div key={name} className="border border-border rounded-xl bg-card p-4">
                    <p className="font-heading text-sm tracking-wide uppercase truncate">{name}</p>
                    <p className="font-heading text-3xl text-brand mt-1">{total}</p>
                    <p className="font-sans text-[10px] text-muted-foreground">across {Object.keys(days).length} day{Object.keys(days).length !== 1 ? 's' : ''}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(days).sort((a, b) => Number(a[0]) - Number(b[0])).map(([d, n]) => (
                        <span key={d} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-secondary/50 text-foreground">{d}: {n}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ServiceMappingPanel() {
  const [services, setServices] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [mapState, setMapState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/admin/square/services`, { withCredentials: true }).catch(() => ({ data: { services: [] } })),
      axios.get(`${API}/api/team/rooms`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/api/admin/square/service-map`, { withCredentials: true }).catch(() => ({ data: { map: {} } })),
    ]).then(([sRes, rRes, mRes]) => {
      setServices(sRes.data.services || []);
      setRooms(rRes.data || []);
      setMapState(mRes.data.map || {});
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/square/service-map`, { map: mapState }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="text-neutral-500 text-sm"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>;

  return (
    <div className="space-y-4" data-testid="service-mapping-panel">
      <p className="font-sans text-xs text-muted-foreground">
        Map each Square service to a local escape room. This pre-fills "Reservations Completed" counts correctly when you click <strong>Sync Square</strong> on a day.
      </p>
      {services.length === 0 ? (
        <div className="border border-yellow-700/40 bg-yellow-900/10 rounded-xl p-4 flex items-start gap-2">
          <AlertTriangle size={16} className="text-yellow-500 shrink-0 mt-0.5" />
          <p className="font-sans text-xs text-muted-foreground">No services found in the last 30 days. Take at least one booking on Square and come back.</p>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-xl bg-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Square Service</th>
                  <th className="text-left px-4 py-2 font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Recent</th>
                  <th className="text-left px-4 py-2 font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Maps to Room</th>
                </tr>
              </thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.name} className="border-b border-border/30 last:border-0" data-testid={`map-row-${s.name}`}>
                    <td className="px-4 py-2.5 font-sans text-sm">{s.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{s.recent_bookings} booking{s.recent_bookings !== 1 ? 's' : ''}</td>
                    <td className="px-4 py-2.5">
                      <select
                        value={mapState[s.name] || ''}
                        onChange={e => setMapState(m => ({ ...m, [s.name]: e.target.value }))}
                        data-testid={`map-select-${s.name}`}
                        className="bg-background border border-border rounded-lg px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand"
                      >
                        <option value="">— No mapping —</option>
                        {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="map-save-btn" className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-2.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Mapping'}
            </button>
            {saved && <span className="font-sans text-xs text-green-400">Saved!</span>}
          </div>
        </>
      )}
    </div>
  );
}

